import { createSlice } from "@reduxjs/toolkit";
import { LoadingStatus } from "../sessions/models/LoadingStatus";
import { ApiErrorResponse } from "../../api/types/ApiErrorResponse";
import { RootState } from "../../app/store";
import { MyPartnerStatus } from "./models/MyPartnerStatus";
import { PartnerTerms } from "./models/PartnerTerms";
import {
  acceptPartnerTerms,
  applyAsPartner,
  fetchMyPartnerStatus,
  fetchPartnerTerms,
} from "./slice-extensions/referrals-slice-extension";

interface ReferralsState {
  partnerTerms: PartnerTerms | null;
  partnerTermsStatus: LoadingStatus;
  partnerTermsError?: ApiErrorResponse;
  acceptStatus: LoadingStatus;
  myPartner: MyPartnerStatus | null;
  myPartnerStatus: LoadingStatus;
  applyStatus: LoadingStatus;
}

const initialState: ReferralsState = {
  partnerTerms: null,
  partnerTermsStatus: LoadingStatus.Idle,
  partnerTermsError: undefined,
  acceptStatus: LoadingStatus.Idle,
  myPartner: null,
  myPartnerStatus: LoadingStatus.Idle,
  applyStatus: LoadingStatus.Idle,
};

const referralsSlice = createSlice({
  name: "referrals",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchPartnerTerms.pending, (state) => {
        state.partnerTermsStatus = LoadingStatus.Loading;
        state.partnerTermsError = undefined;
      })
      .addCase(fetchPartnerTerms.fulfilled, (state, action) => {
        state.partnerTermsStatus = LoadingStatus.Idle;
        state.partnerTerms = action.payload;
      })
      .addCase(fetchPartnerTerms.rejected, (state, action) => {
        state.partnerTermsStatus = LoadingStatus.Failed;
        state.partnerTermsError = action.payload;
      })
      .addCase(acceptPartnerTerms.pending, (state) => {
        state.acceptStatus = LoadingStatus.Loading;
      })
      .addCase(acceptPartnerTerms.fulfilled, (state, action) => {
        state.acceptStatus = LoadingStatus.Idle;
        state.partnerTerms = action.payload;
      })
      .addCase(acceptPartnerTerms.rejected, (state) => {
        state.acceptStatus = LoadingStatus.Failed;
      })
      .addCase(fetchMyPartnerStatus.pending, (state) => {
        state.myPartnerStatus = LoadingStatus.Loading;
      })
      .addCase(fetchMyPartnerStatus.fulfilled, (state, action) => {
        state.myPartnerStatus = LoadingStatus.Idle;
        state.myPartner = action.payload;
      })
      .addCase(fetchMyPartnerStatus.rejected, (state) => {
        state.myPartnerStatus = LoadingStatus.Failed;
      })
      .addCase(applyAsPartner.pending, (state) => {
        state.applyStatus = LoadingStatus.Loading;
      })
      .addCase(applyAsPartner.fulfilled, (state, action) => {
        state.applyStatus = LoadingStatus.Idle;
        state.myPartner = action.payload;
      })
      .addCase(applyAsPartner.rejected, (state) => {
        state.applyStatus = LoadingStatus.Failed;
      });
  },
});

export const selectMyPartnerStatus = (state: RootState) => state.referrals.myPartner;
export const selectMyPartnerLoadingStatus = (state: RootState) => state.referrals.myPartnerStatus;
export const selectApplyStatus = (state: RootState) => state.referrals.applyStatus;
export const selectPartnerTerms = (state: RootState) => state.referrals.partnerTerms;
export const selectPartnerTermsStatus = (state: RootState) => state.referrals.partnerTermsStatus;
export const selectPartnerTermsAcceptStatus = (state: RootState) => state.referrals.acceptStatus;

export default referralsSlice.reducer;
